import type { TFunction } from "i18next";
import { typesText } from "../../workflows/inputCards/typeWords";
import type { DisabledReason, PlannedInput } from "../../../api/content/stepPlans";

/** A step's name by its flat key, for a plan's wording. */
export type StepNameOf = (stepKey: string) => string;

/** Where an input's value comes from, in words (`threads.plan.sources`): what the server said, never decided here. */
export function sourceText(input: PlannedInput, t: TFunction, stepName: StepNameOf): string {
  const step = input.wiredFrom === null ? "" : stepName(input.wiredFrom.stepKey);
  // The project's item the step reads (Q1 option A): by its role, or the item the step names, its own default. A role
  // reads as its label (`threads.plan.roles`, built provisionally); one without a label as its key, spaced.
  if (input.source === "project_item") {
    const role = roleOf(input.value ?? input.default);
    if (role === null) return t("threads.plan.sources.step_default");
    return t("threads.plan.sources.projectRole", { role: t(`threads.plan.roles.${role}`, { defaultValue: role.replaceAll("_", " ") }) });
  }
  // Missing: a value the person can give, or something the Thread is bound to.
  const key = input.source === "missing" && !input.takesValue ? "missingFromThread" : input.source;
  return t(`threads.plan.sources.${key}`, { step });
}

/** The project role a step's own item reading names (`{"role": …}`); `null` for an item it names itself. */
function roleOf(target: unknown): string | null {
  if (typeof target !== "object" || target === null || !("role" in target)) return null;
  const role: unknown = target.role;
  return typeof role === "string" ? role : null;
}

/** A note under the source: the default a pending wire falls back to, or that a wired step gave nothing. */
export function sourceNote(input: PlannedInput, t: TFunction, stepName: StepNameOf): string | null {
  if (input.wiredFrom === null) return null;
  if (input.source === "wired_pending") {
    return input.default === null ? null : t("threads.plan.elseDefault", { value: shownValue(input.default) });
  }
  if (input.source === "workflow_default" || input.source === "step_default") {
    return t("threads.plan.gaveNothing", { step: stepName(input.wiredFrom.stepKey) });
  }
  return null;
}

/** Why a step cannot run, in words; `labelOf` names the input a reason concerns. */
export function reasonText(reason: DisabledReason, t: TFunction, labelOf: (input: string) => string): string {
  return t(`threads.plan.reasons.${reason.code}`, {
    input: reason.input === null ? "" : labelOf(reason.input),
    kinds: typesText(reason.accepts, t),
  });
}

/** "A", "A and B", "A, B and C". */
export function namesText(names: readonly string[], t: TFunction): string {
  if (names.length <= 1) return names[0] ?? "";
  return t("threads.plan.list", { first: names.slice(0, -1).join(", "), last: names[names.length - 1] });
}

export function shownValue(value: unknown): string {
  return typeof value === "string" ? value : JSON.stringify(value);
}
